import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldErrorsImpl, FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import InputField from '../../../components/input-field/InputField'
import { useAppDispatch, useAppSelector } from '../../../hooks/useStore'
import Button from '../../../components/buttons/Button'
import { IntegerNumberRegex, PersianMobileNumberRegex } from '../../../utils/regex'
import { verifyIranianNationalId } from '@persian-tools/persian-tools'
import { useEffect, useState } from 'react'
import { format } from 'react-string-format'
import clsx from 'clsx'
import {
  createOtp,
  resetPasswordWithOtp,
  setCreateOtpStatus,
  setForgetPasswordForm,
} from '../../../store/features/auth/authSlice'
import { ForgetPasswordForm } from '../../../store/features/auth/type'

interface IForms {
  nationalCode: string
  mobile: string
  otp?: string
}

interface IOtp {
  otp?: string
}

const ForgetPassword = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { createOtpLoading, createOtpStatus } = useAppSelector((state) => state.auth.createOtp)
  const { resetOtpLoading } = useAppSelector((state) => state.auth.resetPasswordWithOtp)

  const time = 120

  const [timer, setTimer] = useState(time)
  const [activeTimer, setActiveTimer] = useState(false)

  const schema: yup.SchemaOf<IForms & IOtp> = yup.object({
    nationalCode: yup
      .string()
      .matches(IntegerNumberRegex, {
        message: 'integerNumber',
        excludeEmptyString: true,
      })
      .min(10, 'nationalCodeLength')
      .max(10, 'nationalCodeLength')
      .required('requiedFeild')
      .test('verifyNationaCode', 'notValidNationalCode', (value) => {
        return verifyIranianNationalId(value) as boolean
      }),
    mobile: yup
      .string()
      .matches(IntegerNumberRegex, {
        message: 'integerNumber',
        excludeEmptyString: true,
      })
      .min(11, 'mobileLength')
      .max(11, 'mobileLength')
      .matches(PersianMobileNumberRegex, {
        message: 'mobilePattern',
        excludeEmptyString: true,
      })
      .required('requiedFeild'),
    otp: yup.string().matches(IntegerNumberRegex, {
      message: 'integerNumber',
      excludeEmptyString: true,
    }),
    // .required('requiedFeild'),
  })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IForms>({
    resolver: yupResolver(schema),
  })

  const otpCounter = () => {
    const newTimer = timer - 1
    setTimer(newTimer)
  }

  const handleCreateOtp: SubmitHandler<IForms> = (data) => {
    if (nationalCodeValue && mobileValue) {
      dispatch(createOtp({ nationalCode: data.nationalCode, mobile: data.mobile }))
    }
  }

  const handleGetNewPassword: SubmitHandler<IForms> = (data) => {
    if (nationalCodeValue && mobileValue && otpValue) {
      const newPasswordData = {
        nationaCode: data.nationalCode,
        mobile: data.mobile,
        otpCode: data.otp as string,
      }

      dispatch(resetPasswordWithOtp(newPasswordData))
    }
  }

  useEffect(() => {
    if (createOtpStatus) setActiveTimer(true)
    return () => {
      setActiveTimer(false)
    }
  }, [createOtpStatus])

  useEffect(() => {
    if (activeTimer && timer > 0) {
      setTimeout(otpCounter, 1000)
    }
    if (timer === 0) {
      setTimer(time)
      setActiveTimer(false)
    }
  })

  const handleGoBack = () => {
    dispatch(setForgetPasswordForm(ForgetPasswordForm.LOGIN))
    dispatch(setCreateOtpStatus(false))
  }

  const nationalCodeValue = watch('nationalCode')
  const mobileValue = watch('mobile')
  const otpValue = watch('otp')

  return (
    <div className='w-full lg:w-4/5 mt-6'>
      <form className='w-full h-full mb-8 grid grid-cols-1 gap-4 items-center justify-between'>
        <InputField
          id={'nationalCode'}
          lableTitle={t('nationalCode')}
          errors={errors as FieldErrorsImpl<FieldValues>}
          {...register('nationalCode')}
        />
        <InputField
          id={'mobile'}
          lableTitle={t('mobile')}
          errors={errors as FieldErrorsImpl<FieldValues>}
          {...register('mobile')}
        />
      </form>
      <div className='flex -mt-3'>
        <div className='w-[45%]'>
          <InputField
            id={'otp'}
            lableTitle={t('otp')}
            errors={errors as FieldErrorsImpl<FieldValues>}
            {...register('otp')}
            minLength={6}
            maxLength={6}
          />
        </div>
        <Button
          disabled={!nationalCodeValue || !mobileValue || activeTimer}
          loading={createOtpLoading}
          onClick={handleSubmit(handleCreateOtp)}
          className={clsx(
            'flex items-center justify-center rounded-lg ease-in-out duration-200 px-2 py-2 mr-2 mt-7 text-xs w-[55%]',
            !nationalCodeValue || !mobileValue || activeTimer
              ? 'bg-gray-200 text-gray-400'
              : 'bg-blue-300 hover:bg-blue-400 text-white',
          )}
        >
          {activeTimer ? format(t('timeTogetAginOtp'), timer) : t('getOtp')}
        </Button>
      </div>
      <div className='mt-8 mb-4 w-full'>
        <Button
          disabled={!otpValue || Number(otpValue.length) < 6 || !nationalCodeValue || !mobileValue}
          loading={resetOtpLoading}
          types='success'
          classes={clsx(
            'w-full cursser',
            !otpValue || Number(otpValue.length < 6)
              ? 'bg-gray-200 text-gray-400 hover:bg-gray-200'
              : 'bg-teal-300 hover:bg-teal-400',
          )}
          onClick={handleSubmit(handleGetNewPassword)}
        >
          {t('newPassword')}
        </Button>
      </div>
      <div
        onClick={handleGoBack}
        className='flex mb-4 w-full text-sm items-center justify-center text-gray-400 cursor-pointer hover:text-blue-400 ease-in-out'
      >
        {t('goBack')}
      </div>
      <div
        // onClick={handleSetAuthFormClick}
        className='block md:hidden mt-6 text-xs w-full text-center'
      >
        <span>{t('dontHaveAccount')}</span>
        <span className=' mr-2 text-blue-600'>{t('registering')}</span>
      </div>
    </div>
  )
}

export default ForgetPassword
